import { ReactElement, useContext } from 'react';
import {
  IFragmentContentfulHostedVideo,
  IFragmentRendiaVideo,
  IFragmentVimeoVideo,
  IFragmentYouTubeVideo,
  IFragmentFreshpaintVideo,
} from 'types/contentful';
import { useRouter } from 'next/router';
import SiteDataContext from 'context/SiteDataContext';
import Vimeo, { isVimeoVideoGuard } from 'components/UI/Videos/Vimeo';
import FreshpaintVideo, { isFreshpaintVideoGuard } from 'components/UI/Videos/Freshpaint';
import YouTube, { isYoutubeVideoGuard } from 'components/UI/Videos/Youtube';
import Rendia, { isRendiaVideoGuard } from 'components/UI/Videos/Rendia';
import ContentfulVideo from 'components/UI/Videos/ContentfulVideo';

export enum VideoTypeId {
  Vimeo = 'fragmentVimeoVideo',
  Youtube = 'fragmentYouTubeVideo',
  Contentful = 'fragmentContentfulHostedVideo',
  Rendia = 'fragmentRendiaVideo',
  Freshpaint = 'fragmentFreshpaintVideo',
}

export type VideoTypes =
  | IFragmentContentfulHostedVideo
  | IFragmentVimeoVideo
  | IFragmentYouTubeVideo
  | IFragmentRendiaVideo
  | IFragmentFreshpaintVideo;

function fullUrl(domain: string, path: string): URL {
  const protocol = domain.startsWith('localhost') ? 'http' : 'https';

  return new URL(`${protocol}://${domain}${path}`);
}

interface VideoPlayerProps {
  video: VideoTypes;
  className?: string | undefined;
}

export default function VideoPlayerNoWrapper({ video }: VideoPlayerProps): ReactElement {
  const router = useRouter();
  const { domain } = useContext(SiteDataContext);

  const Comp = (): ReactElement => {
    if (isVimeoVideoGuard(video)) {
      return <Vimeo video={video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube video={video} />;
    } else if (isRendiaVideoGuard(video)) {
      return <Rendia video={video} url={fullUrl(domain, router.asPath)} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return <FreshpaintVideo video={video} />;
    } else {
      return <ContentfulVideo video={video} />;
    }
  };

  return <Comp />;
}
